import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { API_CONFIG } from "../shared/constants";
import { ResponseObj, IMerchant, IUserPreferences } from "../interfaces";
import { encryptData, decryptData } from "../utils/encryption";
import { sentryCaptureException } from "../config/sentry";

interface TokenResponse {
  access: string;
  refresh: string;
  sessionId?: string;
}

const ignoreSnakeCase = ["preference"];

class HttpService {
  private axiosInstance: AxiosInstance;
  private static instance: HttpService;
  private cancelToken: (() => void) | null;
  private isRefreshing: boolean = false;
  private failedQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
  }> = [];
  // Handler to prompt the user when session (access token) has expired
  // The UI can register this to show a modal and return user's decision
  private sessionPromptHandler?: () => Promise<"logout" | "continue">;
  private sessionPromptInProgress: boolean = false;
  private sessionDecisionQueue: Array<(d: "logout" | "continue") => void> = [];
  private logoutHandler?: () => Promise<void> | void;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_CONFIG.baseUrl,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    });

    this.setupInterceptors();
    this.cancelToken = null;
  }

  public static getInstance(): HttpService {
    if (!HttpService.instance) {
      HttpService.instance = new HttpService();
    }
    return HttpService.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Allow specific requests to opt-out of Authorization header
        const skipAuth = (config.headers as Record<string, unknown>)?.[
          "X-Skip-Auth"
        ];
        if (skipAuth) {
          // Clean up the control header and do not set Authorization
          delete (config.headers as Record<string, unknown>)["X-Skip-Auth"];
        } else {
          const token = this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        if (!config.headers.Authorization) {
          config.headers["x-request-language"] =
            localStorage.getItem("lang") || "en";
        }

        // Don't set Content-Type for FormData
        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Plan-restricted module: show upgrade CTA and swallow the error
        if (error.response?.status === 403) {
          const msg = (error.response.data?.detail ?? "") as string;
          if (msg.includes("not available in your current plan")) {
            window.dispatchEvent(
              new CustomEvent("plan-upgrade-required", { detail: { message: msg } })
            );
            return Promise.resolve({ data: null });
          }
        }

        // If error is not 401 or request has already been retried, reject
        if (
          error.response?.status !== 401 ||
          originalRequest._retry ||
          originalRequest.url.includes("login") ||
          originalRequest.url.includes("register") ||
          originalRequest.url.includes("logout")
        ) {
          return Promise.reject(error);
        }

        // If a refresh is already in progress, queue this request and retry later
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then(() => this.axiosInstance(originalRequest))
            .catch((err) => Promise.reject(err));
        }

        // Mark request to avoid infinite retry loops
        originalRequest._retry = true;

        // Ask UI what to do next (logout or continue by refreshing)
        const decision = await this.promptForSessionDecision();

        if (decision === "logout") {
          // Trigger app-level logout if registered
          try {
            await this.logoutHandler?.();
          } catch {
            // ignore handler errors here; still reject to callers
          }
          const err = new Error("Session expired: user chose to logout");
          this.processQueue(err as Error);
          return Promise.reject(err);
        }

        // decision is 'continue' -> proceed to refresh token and retry
        this.isRefreshing = true;
        try {
          const newTokens = await this.refreshToken();
          this.setTokens(newTokens);
          this.processQueue();
          return this.axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed -> clear and redirect to login
          this.processQueue(refreshError as Error);
          this.clearTokens();
          window.location.href = "/auth";
          return Promise.reject(refreshError);
        } finally {
          this.isRefreshing = false;
        }
      },
    );
  }

  /**
   * Registers a handler that will be invoked when the access token expires (401).
   * The handler should present a UI prompt and resolve with user's decision
   * - 'logout': abort pending requests and let app logout
   * - 'continue': refresh the token and retry the original request
   */
  public setSessionPromptHandler(
    handler: () => Promise<"logout" | "continue">,
  ) {
    this.sessionPromptHandler = handler;
  }

  /**
   * Registers a logout handler which executes the real logout flow
   * (clearing state, navigation, etc.) managed by the app layer.
   */
  public setLogoutHandler(handler: () => Promise<void> | void) {
    this.logoutHandler = handler;
  }

  /**
   * Ensures only one session prompt is shown at a time. All concurrent
   * 401 handlers await the same decision via a queue. If no handler is
   * registered, defaults to 'logout'.
   */
  private async promptForSessionDecision(): Promise<"logout" | "continue"> {
    const handler = this.sessionPromptHandler;
    if (!handler) return "logout";

    return new Promise<"logout" | "continue">((resolve) => {
      this.sessionDecisionQueue.push(resolve);

      if (this.sessionPromptInProgress) {
        return; // already showing prompt; we'll resolve when decision arrives
      }

      this.sessionPromptInProgress = true;
      handler()
        .then((decision) => {
          const resolvers = [...this.sessionDecisionQueue];
          this.sessionDecisionQueue = [];
          resolvers.forEach((res) => res(decision));
        })
        .catch(() => {
          const resolvers = [...this.sessionDecisionQueue];
          this.sessionDecisionQueue = [];
          resolvers.forEach((res) => res("logout"));
        })
        .finally(() => {
          this.sessionPromptInProgress = false;
        });
    });
  }

  private async refreshToken(): Promise<TokenResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await axios.post<ResponseObj<TokenResponse>>(
        `${API_CONFIG.baseUrl}/${API_CONFIG.path.refreshToken}/`,
        { refresh: refreshToken },
      );

      if (response.data.is_error) {
        throw new Error(response.data.message || "Failed to refresh token");
      }

      return { ...response.data.data, refresh: refreshToken };
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to refresh token: ${error.message}`);
      }
      throw new Error("Failed to refresh token");
    }
  }

  private processQueue(error: Error | null = null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(null);
      }
    });
    this.failedQueue = [];
  }

  public getAccessToken(): string | null {
    const encryptedToken = localStorage.getItem("access_token");
    return encryptedToken ? decryptData<string>(encryptedToken) : null;
  }

  public getRefreshToken(): string | null {
    const encryptedToken = localStorage.getItem("refresh_token");
    return encryptedToken ? decryptData<string>(encryptedToken) : null;
  }

  public getSessionId(): string | null {
    const encryptedSessionId = localStorage.getItem("session_id");
    return encryptedSessionId ? decryptData<string>(encryptedSessionId) : null;
  }

  public setTokens(tokens: TokenResponse): void {
    localStorage.setItem("access_token", encryptData(tokens.access));
    localStorage.setItem("refresh_token", encryptData(tokens.refresh));
    if (tokens.sessionId) {
      localStorage.setItem("session_id", encryptData(tokens.sessionId));
    }
  }

  public clearTokens(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }

  public getAiSessionToken(): string | null {
    const encryptedToken = localStorage.getItem("ai_session_token");
    return encryptedToken ? decryptData<string>(encryptedToken) : null;
  }

  public getAiTokenExpiry(): number | null {
    const expiry = localStorage.getItem("ai_token_expiry");
    return expiry ? parseInt(expiry, 10) : null;
  }

  public setAiSessionToken(token: string, expiresIn: number): void {
    localStorage.setItem("ai_session_token", encryptData(token));
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem("ai_token_expiry", expiryTime.toString());
  }

  public clearAiSessionToken(): void {
    localStorage.removeItem("ai_session_token");
    localStorage.removeItem("ai_token_expiry");
  }

  public setUserData(user: IMerchant): void {
    localStorage.setItem("user", encryptData(user));
  }

  public setTakeOverUser(isTakeOverUser: boolean): void {
    localStorage.setItem("is_take_over_user", JSON.stringify(isTakeOverUser));
  }

  public getTakeOverUser(): boolean {
    const isTakeOverUser = localStorage.getItem("is_take_over_user");
    return isTakeOverUser ? JSON.parse(isTakeOverUser) : false;
  }

  public clearTakeOverUser(): void {
    localStorage.removeItem("is_take_over_user");
  }

  public getUserData(): IMerchant | null {
    const encryptedUser = localStorage.getItem("user");
    return encryptedUser ? decryptData<IMerchant>(encryptedUser) : null;
  }

  public async updateUserPreferences(
    preferences: IUserPreferences,
  ): Promise<IMerchant> {
    const user = this.getUserData();
    if (!user) {
      throw new Error("No user data found");
    }

    const updatedUser = {
      ...user,
      preferences: {
        ...user.preferences,
        ...preferences,
      },
    };

    const response = await this.put<IMerchant>(
      `${API_CONFIG.path.users}/${user.id}`,
      { preferences: updatedUser.preferences },
    );

    this.setUserData(response);
    return response;
  }

  public clearUserData(): void {
    localStorage.removeItem("user");
  }

  public clearAllData(): void {
    this.clearTokens();
    this.clearUserData();
    this.clearAiSessionToken();
    localStorage.removeItem("tempProfilePic");
  }

  public async getCurrentUser(): Promise<IMerchant> {
    const response = await this.get<IMerchant>(API_CONFIG.path.me);
    return response;
  }

  private getUrl(
    url: string,
    params: Record<string, string | number | boolean | null | undefined> = {},
  ): string {
    const queryString = Object.keys(params)
      .filter((key) => {
        const value = params[key];
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          (typeof value === "number" && Number.isNaN(value))
        )
          return false;
        if (typeof value === "string") {
          const lowerValue = value.toLowerCase();
          if (lowerValue === "undefined" || lowerValue === "null") return false;
        }
        return true;
      })
      .map(
        (key) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(params[key] as string | number | boolean)}`,
      )
      .join("&");
    return queryString ? `${url}/?${queryString}` : url + "/";
  }

  private toSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  }

  private convertKeysToSnakeCase<T>(obj: T): T {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.convertKeysToSnakeCase(item)) as T;
    }
    if (obj !== null && typeof obj === "object") {
      return Object.keys(obj as Record<string, unknown>).reduce(
        (result, key) => {
          if (ignoreSnakeCase.includes(key)) {
            return result;
          }
          const snakeKey = this.toSnakeCase(key);
          (result as Record<string, unknown>)[snakeKey] =
            this.convertKeysToSnakeCase((obj as Record<string, unknown>)[key]);

          return result;
        },
        {} as T,
      );
    }
    return obj;
  }

  private async commonAxios<T>({
    method,
    url,
    data,
    config = {},
    isAccessTokenRequire = true,
    contentType = "application/json",
  }: {
    method: string;
    url: string;
    data?: unknown;
    config?: AxiosRequestConfig;
    isAccessTokenRequire?: boolean;
    contentType?: string;
  }): Promise<T> {
    // Handle FormData differently from regular JSON data
    let requestData: unknown;
    const requestHeaders: Record<string, string> = {};

    if (data instanceof FormData) {
      requestData = data;
      // Don't set Content-Type for FormData
    } else {
      requestHeaders["Content-Type"] = contentType;
      if (data) {
        const convertedData = this.convertKeysToSnakeCase(data);
        requestData =
          contentType === "application/json"
            ? JSON.stringify(convertedData)
            : convertedData;
      } else {
        requestData = undefined;
      }
    }

    if (isAccessTokenRequire) {
      const token = this.getAccessToken();
      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    }

    return new Promise((resolve, reject) => {
      this.axiosInstance({
        method,
        url,
        headers: requestHeaders,
        ...config,
        data: requestData,
        cancelToken: new axios.CancelToken((c) => {
          this.cancelToken = c;
        }),
      })
        .then((response: AxiosResponse<any>) => {
          if (config.responseType === "blob") {
            resolve(response.data as T);
            return;
          }
          const responseData = response.data as ResponseObj<T>;
          if (responseData.is_error) {
            reject(new Error(responseData.message || "An error occurred"));
            return;
          }
          resolve(responseData.data);
        })
        .catch((error) => {
          if (error.response?.data) {
            const apiError = error.response.data as ResponseObj<unknown>;
            if (apiError.message) {
              sentryCaptureException(new Error(apiError.message));
            }
            if (error.response.data?.data !== null) {
              reject(error.response.data);
            } else {
              reject(new Error(apiError.message || "An error occurred"));
            }
          } else {
            reject(error);
            sentryCaptureException(error as Error);
          }
        });
    });
  }

  public cancelRequest(): void {
    if (this.cancelToken) {
      this.cancelToken();
    }
  }

  public async get<T>(
    url: string,
    params: Record<string, string | number | boolean> = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.commonAxios<T>({
      method: "GET",
      url: this.getUrl(url, params),
      config,
    });
  }

  public async post<T, D = Record<string, unknown> | FormData>(
    url: string,
    data?: D,
    queryParams: Record<string, string | number | boolean> = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.commonAxios<T>({
      method: "POST",
      url: this.getUrl(url, queryParams),
      data,
      config,
    });
  }

  public async put<T, D = Record<string, unknown> | FormData>(
    url: string,
    data?: D,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.commonAxios<T>({
      method: "PUT",
      url: url + "/",
      data,
      config,
    });
  }

  public async delete<T>(
    url: string,
    data?: unknown,
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.commonAxios<T>({
      method: "DELETE",
      url: url + "/",
      data,
      config,
    });
  }

  public async patch<T, D = Record<string, unknown> | FormData>(
    url: string,
    data?: D,
    queryParams: Record<string, string | number | boolean> = {},
    config: AxiosRequestConfig = {},
  ): Promise<T> {
    return this.commonAxios<T>({
      method: "PATCH",
      url: this.getUrl(url, queryParams),
      data,
      config,
    });
  }

  /**
   * Calls the logout API endpoint to log the user out on the backend.
   */
  public async logout(): Promise<void> {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();
    const sessionId = this.getSessionId();
    try {
      await this.post(
        API_CONFIG.path.logout,
        { refresh_token: refreshToken, session_id: sessionId },
        {},
        {
          headers: {
            // Ensure we explicitly send Authorization as previously
            Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
            // Prevent interceptor from overriding headers
            "X-Skip-Auth": "1",
          },
        },
      );
    } catch {
      // swallow logout API errors; continue client-side cleanup
    }
  }
}

export const httpService = HttpService.getInstance();
